import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  DollarSign, Plus, Pencil, Trash2, Save, Loader2, CheckCircle, AlertTriangle,
  Tag, Calendar, Percent, X, Copy, Eye, EyeOff, Star
} from 'lucide-react';

export default function PricingAdminPage() {
  const [plans, setPlans] = useState([]);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showNewPromo, setShowNewPromo] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, promoRes] = await Promise.all([
        api.get('/admin/pricing/plans'),
        api.get('/admin/pricing/promo-codes'),
      ]);
      setPlans(pRes.data.plans || []);
      setPromos(promoRes.data.promo_codes || []);
    } catch (e) {
      toast.error('Failed to load pricing data');
    } finally { setLoading(false); }
  };

  const savePlan = async (data) => {
    try {
      const res = data.id
        ? await api.put(`/admin/pricing/plans/${data.id}`, data)
        : await api.post('/admin/pricing/plans', data);
      const sync = res.data?.stripe_sync;
      if (sync && !sync.ok) {
        toast.warning(`Plan saved — Stripe sync failed: ${sync.error}`);
      } else {
        toast.success(data.id ? 'Plan updated (Stripe synced)' : 'Plan created (Stripe synced)');
      }
      await load();
      setEditingPlan(null); setShowNewPlan(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const deletePlan = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Stripe product will be deactivated (not deleted).`)) return;
    try {
      await api.delete(`/admin/pricing/plans/${p.id}`);
      toast.success('Plan deleted');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const savePromo = async (data) => {
    try {
      if (data.id) await api.put(`/admin/pricing/promo-codes/${data.id}`, data);
      else await api.post('/admin/pricing/promo-codes', data);
      toast.success(data.id ? 'Promo updated' : 'Promo created');
      await load();
      setEditingPromo(null); setShowNewPromo(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const deletePromo = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"?`)) return;
    try {
      await api.delete(`/admin/pricing/promo-codes/${p.id}`);
      toast.success('Promo deleted');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 animate-spin text-[#0E7490]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="pricing-admin-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <DollarSign className="w-8 h-8 text-[#0E7490]" />
          Pricing & Promo Codes
        </h1>
        <p className="text-[#64748B] mt-1 text-sm">
          Manage plans and promo codes. Changes sync to Stripe automatically — saving a plan creates/updates the Stripe Product and creates a new immutable Price record.
        </p>
      </div>

      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans" data-testid="tab-plans">Plans ({plans.length})</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">Promo Codes ({promos.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button className="careiq-btn-primary flex items-center gap-2" onClick={() => setShowNewPlan(true)} data-testid="add-plan-btn">
              <Plus className="w-4 h-4" /> Add Plan
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map(p => <PlanCard key={p.id} plan={p} onEdit={() => setEditingPlan(p)} onDelete={() => deletePlan(p)} />)}
            {plans.length === 0 && <div className="col-span-full text-center py-12 text-sm text-[#94A3B8]">No plans yet. Click "Add Plan" to create one.</div>}
          </div>
        </TabsContent>

        <TabsContent value="promos" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button className="careiq-btn-primary flex items-center gap-2" onClick={() => setShowNewPromo(true)} data-testid="add-promo-btn">
              <Plus className="w-4 h-4" /> Add Promo Code
            </Button>
          </div>
          <Card className="careiq-card">
            <CardContent className="p-0">
              {promos.length === 0 ? (
                <div className="text-center py-12 text-sm text-[#94A3B8]">No promo codes. Click "Add Promo Code" to create one.</div>
              ) : (
                <div className="divide-y divide-[#E2E8F0]">
                  {promos.map(p => <PromoRow key={p.id} promo={p} onEdit={() => setEditingPromo(p)} onDelete={() => deletePromo(p)} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {(showNewPlan || editingPlan) && (
        <PlanEditor
          initial={editingPlan}
          onClose={() => { setEditingPlan(null); setShowNewPlan(false); }}
          onSave={savePlan}
        />
      )}
      {(showNewPromo || editingPromo) && (
        <PromoEditor
          initial={editingPromo}
          plans={plans}
          onClose={() => { setEditingPromo(null); setShowNewPromo(false); }}
          onSave={savePromo}
        />
      )}
    </div>
  );
}

function PlanCard({ plan, onEdit, onDelete }) {
  return (
    <Card className={`careiq-card relative ${plan.popular ? 'ring-2 ring-[#0E7490]' : ''}`} data-testid={`plan-${plan.plan_id}`}>
      {plan.popular && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#0E7490] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
          <Star className="w-2.5 h-2.5" /> POPULAR
        </div>
      )}
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-[#0A2342]">{plan.name}</div>
            <div className="text-[10px] text-[#94A3B8] font-mono">{plan.plan_id}</div>
            {!plan.visible && <div className="text-[10px] text-[#991B1B] mt-1"><EyeOff className="w-3 h-3 inline" /> HIDDEN</div>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`edit-${plan.plan_id}`}><Pencil className="w-3 h-3" /></Button>
            <Button size="sm" variant="ghost" className="text-[#991B1B]" onClick={onDelete} data-testid={`delete-${plan.plan_id}`}><Trash2 className="w-3 h-3" /></Button>
          </div>
        </div>
        <div className="text-[#64748B] text-xs">{plan.description}</div>
        <div className="border-t border-[#E2E8F0] pt-3">
          <div className="text-2xl font-bold text-[#0A2342]">${plan.monthly_price.toFixed(0)}<span className="text-xs text-[#64748B] font-normal">/mo</span></div>
          <div className="text-xs text-[#64748B]">${plan.yearly_price.toFixed(0)}/yr (save {plan.yearly_discount_pct}%)</div>
        </div>
        <div className="space-y-1">
          {(plan.features || []).slice(0, 4).map((f, i) => (
            <div key={i} className="text-xs text-[#64748B] flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#166534] mt-0.5 shrink-0" /><span>{f}</span></div>
          ))}
          {(plan.features || []).length > 4 && <div className="text-[10px] text-[#94A3B8]">+{plan.features.length - 4} more features</div>}
        </div>
        <div className="text-[10px] text-[#94A3B8] font-mono truncate pt-2 border-t border-[#E2E8F0]">
          {plan.stripe_product_id ? <><CheckCircle className="w-2.5 h-2.5 inline text-[#166534]" /> Stripe: {plan.stripe_product_id}</> : <><AlertTriangle className="w-2.5 h-2.5 inline text-[#CA8A04]" /> Not synced to Stripe</>}
        </div>
      </CardContent>
    </Card>
  );
}

function PromoRow({ promo, onEdit, onDelete }) {
  const copy = async () => { try { await navigator.clipboard.writeText(promo.code); toast.success('Copied'); } catch {} };
  const expired = promo.expires_at && promo.expires_at < new Date().toISOString();
  const usedUp = promo.max_uses && promo.times_used >= promo.max_uses;
  const badge = !promo.active ? { t: 'Inactive', c: 'bg-[#FEE2E2] text-[#991B1B]' } : expired ? { t: 'Expired', c: 'bg-[#FEF3C7] text-[#92400E]' } : usedUp ? { t: 'Used up', c: 'bg-[#FEF3C7] text-[#92400E]' } : { t: 'Active', c: 'bg-[#DCFCE7] text-[#166534]' };
  return (
    <div className="p-4 flex items-center justify-between gap-3 flex-wrap hover:bg-[#F8FAFC]" data-testid={`promo-${promo.code}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-[#F0FDFA] flex items-center justify-center shrink-0"><Tag className="w-4 h-4 text-[#0E7490]" /></div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm font-semibold text-[#0A2342]">{promo.code}</code>
            <button onClick={copy} className="text-[#0E7490] hover:text-[#0A2342]" title="Copy"><Copy className="w-3 h-3" /></button>
            <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${badge.c}`}>{badge.t}</span>
          </div>
          <div className="text-xs text-[#64748B] flex items-center gap-2 flex-wrap">
            <span>
              {promo.discount_type === 'percent' ? `${promo.discount_value}% off` : `$${promo.discount_value} off`}
            </span>
            {promo.expires_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(promo.expires_at).toLocaleDateString()}</span>}
            <span>{promo.times_used}{promo.max_uses ? `/${promo.max_uses}` : ''} uses</span>
            {(promo.applies_to_plans || []).length > 0 && <span>on {promo.applies_to_plans.join(', ')}</span>}
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={onEdit}><Pencil className="w-3 h-3" /></Button>
        <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={onDelete}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

function PlanEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState(() => initial || {
    plan_id: '', name: '', description: '', monthly_price: 49, yearly_price: 470,
    yearly_discount_pct: 20, features: [''], popular: false, visible: true,
    feature_keys: [], max_users: 5, max_patients: 500,
  });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);

  React.useEffect(() => {
    api.get('/public/module-catalog').then(r => setCatalog(r.data?.modules || [])).catch(() => {});
  }, []);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const toggleModule = (key) => {
    setForm(f => {
      const cur = new Set(f.feature_keys || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...f, feature_keys: Array.from(cur) };
    });
  };

  // Auto-calc yearly from monthly * discount when monthly changes
  const onMonthlyChange = (v) => {
    const m = parseFloat(v) || 0;
    const dc = parseFloat(form.yearly_discount_pct) || 0;
    const yearly = +(m * 12 * (1 - dc / 100)).toFixed(2);
    set({ monthly_price: m, yearly_price: yearly });
  };
  const onDiscountChange = (v) => {
    const dc = parseFloat(v) || 0;
    const m = parseFloat(form.monthly_price) || 0;
    const yearly = +(m * 12 * (1 - dc / 100)).toFixed(2);
    set({ yearly_discount_pct: dc, yearly_price: yearly });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Name required'); return; }
    if (!initial && !form.plan_id?.trim()) { toast.error('Plan ID required'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        features: (form.features || []).map(f => (f || '').trim()).filter(Boolean),
        feature_keys: form.feature_keys || [],
        max_users: parseInt(form.max_users || 0, 10) || 0,
        max_patients: parseInt(form.max_patients || 0, 10) || 0,
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-editor-dialog">
        <DialogHeader><DialogTitle>{initial ? 'Edit Plan' : 'Add Plan'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="careiq-label">Name *</Label>
              <Input value={form.name || ''} onChange={e => set({ name: e.target.value })} className="careiq-input" data-testid="plan-name-input" />
            </div>
            <div>
              <Label className="careiq-label">Plan ID (slug) {initial && <span className="text-[10px] text-[#94A3B8]">(locked)</span>}</Label>
              <Input value={form.plan_id || ''} onChange={e => set({ plan_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} disabled={!!initial} className="careiq-input font-mono" placeholder="e.g., pro" data-testid="plan-id-input" />
            </div>
          </div>
          <div>
            <Label className="careiq-label">Description</Label>
            <Input value={form.description || ''} onChange={e => set({ description: e.target.value })} className="careiq-input" />
          </div>
          <div className="grid md:grid-cols-3 gap-3 border-t pt-3">
            <div>
              <Label className="careiq-label">Monthly Price ($) *</Label>
              <Input type="number" step="0.01" value={form.monthly_price} onChange={e => onMonthlyChange(e.target.value)} className="careiq-input" data-testid="plan-monthly-input" />
            </div>
            <div>
              <Label className="careiq-label">Yearly Discount (%)</Label>
              <Input type="number" min="0" max="100" value={form.yearly_discount_pct} onChange={e => onDiscountChange(e.target.value)} className="careiq-input" />
            </div>
            <div>
              <Label className="careiq-label">Yearly Price ($)</Label>
              <Input type="number" step="0.01" value={form.yearly_price} onChange={e => set({ yearly_price: parseFloat(e.target.value) || 0 })} className="careiq-input" />
            </div>
          </div>
          <div>
            <Label className="careiq-label">Features (one per line)</Label>
            <textarea
              value={(form.features || []).join('\n')}
              onChange={e => set({ features: e.target.value.split('\n') })}
              className="careiq-input min-h-[80px] font-mono text-xs"
              placeholder={'Up to 25 users\n5,000 patients\nAI CDS\nReports'}
              data-testid="plan-features-input"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1">Marketing copy shown to customers. The toggles below control what's actually unlocked.</p>
          </div>

          {/* Capacity */}
          <div className="grid md:grid-cols-2 gap-3 border-t pt-3">
            <div>
              <Label className="careiq-label">Max users (0 = unlimited)</Label>
              <Input type="number" min="0" value={form.max_users ?? 0}
                     onChange={e => set({ max_users: parseInt(e.target.value || '0', 10) })}
                     className="careiq-input" data-testid="plan-max-users-input" />
            </div>
            <div>
              <Label className="careiq-label">Max patients (0 = unlimited)</Label>
              <Input type="number" min="0" value={form.max_patients ?? 0}
                     onChange={e => set({ max_patients: parseInt(e.target.value || '0', 10) })}
                     className="careiq-input" data-testid="plan-max-patients-input" />
            </div>
          </div>

          {/* Module toggles */}
          <div className="border-t pt-3">
            <Label className="careiq-label">Modules unlocked by this plan</Label>
            <p className="text-[10px] text-[#94A3B8] mb-2">Toggle each module to control what subscribers can access. Sidebar items + routes will be gated accordingly.</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="plan-modules-grid">
              {catalog.map(mod => {
                const on = (form.feature_keys || []).includes(mod.key);
                return (
                  <label key={mod.key}
                         className={`flex items-center gap-2 cursor-pointer text-sm px-2 py-1.5 rounded border ${on ? 'bg-[#F0FDFA] border-[#0E7490]/30' : 'bg-white border-[#E2E8F0]'}`}
                         data-testid={`module-toggle-${mod.key}`}>
                    <Switch checked={on} onCheckedChange={() => toggleModule(mod.key)} />
                    <span className={on ? 'text-[#0A2342] font-medium' : 'text-[#64748B]'}>{mod.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={!!form.popular} onCheckedChange={v => set({ popular: v })} />
              <span className="text-sm">Mark as "Popular"</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={form.visible !== false} onCheckedChange={v => set({ visible: v })} />
              <span className="text-sm">Visible on landing page</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save & Sync to Stripe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromoEditor({ initial, plans, onClose, onSave }) {
  const [form, setForm] = useState(() => initial || {
    code: '', discount_type: 'percent', discount_value: 20, expires_at: '',
    max_uses: '', applies_to_plans: [], active: true,
  });
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const togglePlan = (planId) => setForm(f => ({
    ...f,
    applies_to_plans: (f.applies_to_plans || []).includes(planId)
      ? f.applies_to_plans.filter(x => x !== planId)
      : [...(f.applies_to_plans || []), planId],
  }));

  const handleSave = async () => {
    if (!form.code?.trim()) { toast.error('Code required'); return; }
    if (!form.discount_value || form.discount_value <= 0) { toast.error('Discount must be > 0'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        code: form.code.trim().toUpperCase(),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="promo-editor-dialog">
        <DialogHeader><DialogTitle>{initial ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="careiq-label">Code *</Label>
            <Input value={form.code || ''} onChange={e => set({ code: e.target.value.toUpperCase() })} disabled={!!initial} className="careiq-input font-mono uppercase" placeholder="LAUNCH25" data-testid="promo-code-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="careiq-label">Discount Type</Label>
              <Select value={form.discount_type} onValueChange={v => set({ discount_type: v })}>
                <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent (%)</SelectItem>
                  <SelectItem value="fixed">Fixed amount ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="careiq-label">{form.discount_type === 'percent' ? 'Percent Off *' : 'Dollars Off *'}</Label>
              <Input type="number" step="0.01" min="0" value={form.discount_value || ''} onChange={e => set({ discount_value: parseFloat(e.target.value) || 0 })} className="careiq-input" data-testid="promo-value-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="careiq-label">Expires (optional)</Label>
              <Input type="datetime-local" value={form.expires_at ? form.expires_at.slice(0, 16) : ''} onChange={e => set({ expires_at: e.target.value })} className="careiq-input" />
            </div>
            <div>
              <Label className="careiq-label">Max Uses (optional)</Label>
              <Input type="number" min="1" value={form.max_uses || ''} onChange={e => set({ max_uses: e.target.value })} placeholder="Unlimited" className="careiq-input" />
            </div>
          </div>
          <div>
            <Label className="careiq-label">Applies to plans (leave empty for ALL)</Label>
            <div className="flex gap-2 flex-wrap border rounded p-2">
              {plans.map(p => (
                <label key={p.plan_id} className="flex items-center gap-1 px-2 py-1 rounded border cursor-pointer hover:bg-[#F8FAFC]">
                  <input type="checkbox" checked={(form.applies_to_plans || []).includes(p.plan_id)} onChange={() => togglePlan(p.plan_id)} />
                  <span className="text-xs">{p.name}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={form.active !== false} onCheckedChange={v => set({ active: v })} />
            <span className="text-sm">Active</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-promo-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
